import { IKpiAppUsage, KpiAppUsageSource } from '@/types/response';
import moment from 'moment';
import { CATEGORIES, Category, labelOf } from './labels';

/**
 * 期間の種類。運用は月2回（15日ごろに当月1〜15日の速報、月初に前月1日〜末日の確定値）。
 * 比べる相手は同じ種類の前月（確定は前月の確定、前半は前月の前半）。それ以外の期間は比べない。
 */
export type PeriodKind = 'month' | 'firstHalf' | 'custom';

export interface IPeriod {
  key: string;
  source: KpiAppUsageSource;
  periodStart: string;
  periodEnd: string;
  kind: PeriodKind;
  label: string;
}

export const periodKey = (p: {
  source: string;
  periodStart: string;
  periodEnd: string;
}) => `${p.source}:${p.periodStart}:${p.periodEnd}`;

export const kindOf = (periodStart: string, periodEnd: string): PeriodKind => {
  const start = moment(periodStart, 'YYYY-MM-DD');
  const end = moment(periodEnd, 'YYYY-MM-DD');
  if (start.date() !== 1 || !start.isSame(end, 'month')) return 'custom';
  if (end.date() === end.daysInMonth()) return 'month';
  if (end.date() === 15) return 'firstHalf';

  return 'custom';
};

export const toPeriod = (
  source: KpiAppUsageSource,
  periodStart: string,
  periodEnd: string,
): IPeriod => {
  // 新規ファネルはインストール週。前月比・月次推移は使わないので custom のまま
  const kind =
    source === 'appsflyer_cohort' ? 'custom' : kindOf(periodStart, periodEnd);
  const start = moment(periodStart, 'YYYY-MM-DD');
  const label =
    source === 'appsflyer_cohort'
      ? `${start.format('YYYY/MM/DD')}〜${moment(periodEnd, 'YYYY-MM-DD').format('MM/DD')} インストール`
      : kind === 'month'
        ? `${start.format('YYYY/MM')}（確定）`
        : kind === 'firstHalf'
          ? `${start.format('YYYY/MM')} 前半（1〜15日）`
          : `${start.format('YYYY/MM/DD')}〜${moment(periodEnd, 'YYYY-MM-DD').format('MM/DD')}`;

  return {
    key: periodKey({ source, periodStart, periodEnd }),
    source,
    periodStart,
    periodEnd,
    kind,
    label,
  };
};

/** 取得元ごとの期間（新しい順） */
export const periodsOf = (
  rows: IKpiAppUsage[],
  source: KpiAppUsageSource,
): IPeriod[] => {
  const map = new Map<string, IPeriod>();
  rows
    .filter((r) => r.source === source)
    .forEach((r) => {
      const key = periodKey(r);
      if (!map.has(key))
        map.set(key, toPeriod(source, r.periodStart, r.periodEnd));
    });

  return Array.from(map.values()).sort((a, b) =>
    b.periodStart === a.periodStart
      ? b.periodEnd.localeCompare(a.periodEnd)
      : b.periodStart.localeCompare(a.periodStart),
  );
};

/** 比べる相手の期間（前月の同じ種類）。custom は比べない */
export const comparisonOf = (
  p: IPeriod,
): { periodStart: string; periodEnd: string } | null => {
  if (p.kind === 'custom') return null;
  const prev = moment(p.periodStart, 'YYYY-MM-DD').subtract(1, 'month');

  return {
    periodStart: prev.format('YYYY-MM-DD'),
    periodEnd: (p.kind === 'month'
      ? prev.clone().endOf('month')
      : prev.clone().date(15)
    ).format('YYYY-MM-DD'),
  };
};

/** 増減率（%）。比べる相手が無い・0 のときは null */
export const changePct = (
  cur: number,
  prev: number | undefined,
): number | null =>
  prev ? Math.round(((cur - prev) / prev) * 1000) / 10 : null;

export interface IUsageRow {
  name: string;
  label: string;
  category: Category;
  count: number;
  users: number;
  engagementSeconds: number | null;
  prevUsers: number | undefined;
  usersChangePct: number | null;
}

/** 選んだ期間の行に、日本語名・分類と前月同期間との比較を付ける（ユーザーの多い順） */
export const usageRows = (
  rows: IKpiAppUsage[],
  period: IPeriod,
): IUsageRow[] => {
  const comparison = comparisonOf(period);
  const prev = new Map<string, number>();
  if (comparison) {
    rows
      .filter(
        (r) =>
          periodKey(r) === periodKey({ source: period.source, ...comparison }),
      )
      .forEach((r) => prev.set(r.name, r.users));
  }

  return rows
    .filter((r) => periodKey(r) === period.key)
    .map((r) => {
      const { label, category } = labelOf(period.source, r.name);
      const prevUsers = comparison ? prev.get(r.name) : undefined;

      return {
        name: r.name,
        label,
        category,
        count: r.count,
        users: r.users,
        engagementSeconds: r.engagementSeconds,
        prevUsers,
        usersChangePct: changePct(r.users, prevUsers),
      };
    })
    .sort((a, b) => b.users - a.users || a.name.localeCompare(b.name));
};

export interface ICategorySummary {
  category: Category;
  /** 分類内でいちばん多い画面（機能）のユーザー数。画面ごとの人数は重なるので足さない */
  users: number;
  topLabel: string;
  /** 表示回数（イベント数）の合計。こちらは足せる */
  count: number;
  usersChangePct: number | null;
}

/**
 * 分類ごとのまとめ。前月比は「前月のその分類でいちばん多い画面」と比べる。
 * 今月の CSV から消えた画面も前月の最大には含める（含めないと、主要な画面が消えたのに増加に見える）。
 */
export const categorySummaries = (
  all: IKpiAppUsage[],
  period: IPeriod,
  rows: IUsageRow[],
): ICategorySummary[] => {
  const comparison = comparisonOf(period);
  const prevMax = new Map<Category, number>();
  if (comparison) {
    const prevKey = periodKey({ source: period.source, ...comparison });
    all
      .filter((r) => periodKey(r) === prevKey)
      .forEach((r) => {
        const { category } = labelOf(period.source, r.name);
        prevMax.set(category, Math.max(prevMax.get(category) ?? 0, r.users));
      });
  }

  return CATEGORIES.map((category) => {
    const inCategory = rows.filter((r) => r.category === category);
    if (inCategory.length === 0) return null;
    const top = inCategory.reduce((a, b) => (b.users > a.users ? b : a));

    return {
      category,
      users: top.users,
      topLabel: top.label,
      count: inCategory.reduce((sum, r) => sum + r.count, 0),
      usersChangePct: changePct(top.users, prevMax.get(category)),
    };
  }).filter((s): s is ICategorySummary => s !== null);
};

/**
 * 月ごとのユーザー数の推移（確定値の期間だけ）。recharts にそのまま渡す形
 * `[{ month: '2026/09', HomeScreen: 1400, ... }]` で、データの無い月は値を持たない。
 */
export const monthlyTrend = (
  rows: IKpiAppUsage[],
  source: KpiAppUsageSource,
  names: string[],
): Record<string, string | number>[] => {
  const months = periodsOf(rows, source)
    .filter((p) => p.kind === 'month')
    .reverse();
  if (months.length === 0) return [];

  // 取り込みの無い月も軸に残す（詰めると、欠けた月をまたいで線がつながって見える）
  const byMonth = new Map(months.map((p) => [p.periodStart, p]));
  const last = moment(months[months.length - 1].periodStart, 'YYYY-MM-DD');
  const points: Record<string, string | number>[] = [];
  for (
    const m = moment(months[0].periodStart, 'YYYY-MM-DD');
    !m.isAfter(last);
    m.add(1, 'month')
  ) {
    const point: Record<string, string | number> = {
      month: m.format('YYYY/MM'),
    };
    const p = byMonth.get(m.format('YYYY-MM-DD'));
    if (p) {
      rows
        .filter((r) => periodKey(r) === p.key && names.includes(r.name))
        .forEach((r) => {
          point[r.name] = r.users;
        });
    }
    points.push(point);
  }

  return points;
};

/** 平均滞在時間（秒）を「4分36秒」の形に */
export const formatDuration = (seconds: number | null | undefined): string => {
  if (typeof seconds !== 'number') return '—';
  const s = Math.round(seconds);
  if (s < 60) return `${s}秒`;

  return `${Math.floor(s / 60)}分${String(s % 60).padStart(2, '0')}秒`;
};
